"use client";

import { type ReactNode } from "react";
import { WritingToolOrb } from "./writing-tool-orb";
import { useWritingTools } from "./writing-tools-context";
import { Sparkles } from "lucide-react";

import { AssistantSidebar } from "@/components/editor/assistant-sidebar";
import type { AssistantExperience } from "@/lib/ai/assistant-v2/entitlement";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";

interface MobileAssistantSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    storyId: string;
    chapterId: string;
    assistantExperience?: AssistantExperience;
    assistantFlareAvailable?: boolean;
    floatingControl?: ReactNode;
}

/** Shared production/demo mobile Assistant surface. Chat data and active
 * streams are scoped outside this presentation, so closing it is non-destructive. */
export function MobileAssistantSheet({
    open,
    onOpenChange,
    storyId,
    chapterId,
    assistantExperience,
    assistantFlareAvailable,
    floatingControl,
}: MobileAssistantSheetProps) {
    const tools = useWritingTools();
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                className="flex h-[min(88dvh,52rem)] max-h-[calc(100dvh-env(safe-area-inset-top))] flex-col gap-0 p-0 pb-[env(safe-area-inset-bottom)]"
            >
                <SheetHeader className="flex-row items-center gap-2 space-y-0 border-b border-border p-3">
                    <Sparkles className="size-4 text-primary" aria-hidden />
                    <SheetTitle>AI Assistant</SheetTitle>
                    <SheetDescription className="sr-only">
                        Chat with the AI writing assistant about this chapter.
                    </SheetDescription>
                </SheetHeader>
                <div className="flex min-h-0 flex-1 flex-col">
                    <AssistantSidebar
                        storyId={storyId}
                        chapterId={chapterId}
                        assistantExperience={assistantExperience}
                        assistantFlareAvailable={assistantFlareAvailable}
                    />
                </div>
                {floatingControl}
                {open && tools?.activeTool && <WritingToolOrb />}
            </SheetContent>
        </Sheet>
    );
}
